import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, useAdminGet } from '../../kit';
import { AdminLink } from '../../adminNav';
import { fmtN } from '../../monthly';
import { Badge, Drawer } from '../emailKit';
import { fmtIST } from '../trace/DeliveryTrace';
import { LinkRankTable } from '../links/LinkClicks';
import Timeline from '../people/Timeline';
import type { TimelineTarget } from '../people/shared';
import { FunnelSteps } from '../campaigns/shared';
import { EngTags, EngagementNote, Pct, SECTION, arr, num, pctText, rateOf, str } from '../dashboard/shared';
import {
  type EmailGroup, GroupNote, RUN_KIND_WORD, STEP_WORDS, signinRate, toLinkRows, toSteps,
} from './shared';

/**
 * One invite run (email/invite_run_detail): the funnel from sent to signed in,
 * the links people clicked, and every recipient's path — delivered, opened,
 * clicked, signed in — with the status cards above the list as its filter.
 * The CampaignDrawer pattern (campaigns/CampaignHistory.tsx), for invites.
 *
 * `period` is left out for a deep link (the server then reads the last 365
 * days, so an old run opens whole); the runs table passes the days that hold
 * the row it was opened from. `groupId` (the Invites group filter, or a deep
 * link's bulk=) narrows the run to that group's members, as the row it was
 * opened from; "Show the whole run" drops it.
 *
 * Opened / clicked (and their times and links) are people's unless
 * `automated`; each recipient carries its automated-engagement tags
 * ("Instant read (auto)", "Apple MPP", "Bot click") with the reason on hover.
 */

const flag = (on: any, at?: any) => (on
  ? <span className="text-green-800" title={at ? fmtIST(at) : undefined}>✓{at ? ` ${fmtIST(at)}` : ''}</span>
  : <span className="text-grayscale-1100">—</span>);

const failed = (r: any) => /fail|error|not_sent/i.test(str(r?.status));

/** The status cards, each a filter on the recipients list. */
const FILTERS: { key: string; label: string; test: (r: any) => boolean }[] = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'delivered', label: 'Delivered', test: r => !!r.delivered },
  { key: 'opened', label: 'Opened', test: r => !!r.opened },
  { key: 'clicked', label: 'Clicked', test: r => !!r.clicked },
  { key: 'not_opened', label: 'Not opened', test: r => !!r.delivered && !r.opened && !r.clicked },
  { key: 'auto_opened', label: 'Auto opens only', test: r => !!r.auto_opened },
  { key: 'bot_clicked', label: 'Bot clicks only', test: r => !!r.bot_clicked },
  { key: 'bounced', label: 'Bounced', test: r => !!r.bounced },
  { key: 'failed', label: 'Failed', test: failed },
  { key: 'signed_in', label: 'Signed in', test: r => !!r.signed_in },
];

const statusBadge = (r: any) => {
  const s = r.bounced ? 'bounced' : str(r.status);
  if (!s) return <span className="text-grayscale-1100">—</span>;
  const tone = s === 'bounced' || failed(r) ? 'red' : s === 'sent' || s === 'delivered' ? 'green'
    : s === 'skipped' ? 'grey' : 'amber';
  return <span title={str(r.error) || undefined}><Badge tone={tone}>{s}</Badge></span>;
};

const InviteRunDrawer: React.FC<{
  runId: string; row?: any; period?: Record<string, string>; excludeTeam: string; attr: string;
  groupId?: string; groupInfo?: EmailGroup; automated?: boolean;
  onClose: () => void;
}> = ({ runId, row, period, excludeTeam, attr, groupId, groupInfo, automated, onClose }) => {
  const [whole, setWhole] = useState(false);
  const narrow = groupId && !whole ? groupId : '';
  const params = useMemo(() => ({
    run_id: runId, exclude_team: excludeTeam, attr_days: attr, ...(period || {}), ...(narrow ? { group: narrow } : {}),
    ...(automated ? { automated: '1' } : {}),
  }), [runId, excludeTeam, attr, period, narrow, automated]);
  const pKey = JSON.stringify(params);
  // Refresh asks the server to rebuild (refresh=1 drops its 90-second cache),
  // the Dashboard's way: a run that has just gone out fills in as its sends
  // are logged and SendGrid reports.
  const [fresh, setFresh] = useState(false);
  const [stamp, setStamp] = useState(0);
  const q = useAdminGet(SECTION, 'invite_run_detail', fresh ? { ...params, refresh: '1' } : params, [pKey, stamp]);
  useEffect(() => { if (fresh && !q.loading) setFresh(false); }, [fresh, q.loading]);
  const refresh = () => { setFresh(true); setStamp(s => s + 1); };
  const [status, setStatus] = useState('all');
  const [timeline, setTimeline] = useState<TimelineTarget | null>(null);
  const d: any = q.data || {};
  // The server echoes the group it narrowed to (filters.group); one that
  // leaves the key out did not narrow, and the drawer says so.
  const filters: any = d.filters && typeof d.filters === 'object' ? d.filters : null;
  const echo: any = filters?.group && typeof filters.group === 'object' ? filters.group : null;
  const ignored = !!narrow && !!q.data && !(filters && 'group' in filters);
  const groupLabel = groupInfo?.label || str(echo?.label) || groupId || '';
  const run: any = d.run && typeof d.run === 'object' ? d.run : (row || {});
  const steps = toSteps(d.steps, run);
  const links = toLinkRows(d.links);
  const recipients: any[] = useMemo(() => arr(d.recipients).map((r: any) => ({
    ...r, email: str(r?.email), name: str(r?.name),
    links: Array.isArray(r?.links) ? r.links.map(str).filter(Boolean) : [],
  })), [d.recipients]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(f => [f.key, recipients.filter(f.test).length])),
    [recipients]);
  const test = FILTERS.find(f => f.key === status)?.test || (() => true);
  const shown = status === 'all' ? recipients : recipients.filter(test);
  const kind = str(run.kind);
  const group = run.group && typeof run.group === 'object' && str(run.group.id) ? run.group : null;
  const title = str(run.label) || (kind === 'single' || runId === 'single' ? 'One-off invites' : `Invite run ${runId}`);
  const subtitle = [
    RUN_KIND_WORD[kind] || '',
    run.at ? fmtIST(run.at) + (run.last_at && fmtIST(run.last_at) !== fmtIST(run.at) ? ` – ${fmtIST(run.last_at)}` : '') : '',
    str(run.by) ? `by ${str(run.by)}` : '',
    // A bulk add's own group carries the run's label: said once.
    group && str(group.label) !== title ? str(group.label) : '',
    runId,
  ].filter(Boolean).join(' · ');
  const openTimeline = (r: any) => setTimeline(r.user_id ? { userId: str(r.user_id), email: r.email || undefined }
    : { email: r.email.toLowerCase() });
  const canOpen = (r: any) => !!(r.user_id || r.email);
  const sr = signinRate(run);

  return (
    <>
      <Drawer title={title} subtitle={subtitle} onClose={onClose}>
        {narrow && !ignored && (
          <GroupNote group={groupInfo} id={narrow} fallbackLabel={str(echo?.label)} people={echo?.people}
            what="this run's invites to">
            {' '}<button type="button" className="underline underline-offset-2" onClick={() => setWhole(true)}>
              Show the whole run</button>
          </GroupNote>
        )}
        {groupId && (whole || ignored) && (
          <div className="text-xs text-grayscale-1200 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
            Every recipient of this run is shown, not only the people in <b>{groupLabel}</b>.
            {whole && <>{' '}<button type="button" className="underline underline-offset-2"
              onClick={() => setWhole(false)}>Only them</button></>}
          </div>
        )}
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading && !q.data ? <Loading label="Loading this run…" /> : !q.data && q.error ? (
          <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
            <Btn small onClick={refresh}
              title="Ask again, counting from the database (otherwise results are kept for 90 seconds)">Try again</Btn>
            A run that has only just started can take a minute to show up here.
          </div>
        ) : (
          <>
            <Section title="Funnel"
              right={<span className="flex items-center gap-2 text-xs text-grayscale-1100">
                signed in = first sign-in within {num(attr)} days of the invite
                {/* A run that has just gone out fills in as SendGrid reports. */}
                <Btn small onClick={refresh}
                  title="Count again from the database (otherwise results are kept for 90 seconds)">Refresh</Btn>
              </span>}>
              <FunnelSteps steps={steps} words={STEP_WORDS} />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grayscale-1100">
                <span>Open rate <b className="text-grayscale-1300"><Pct v={rateOf(run, 'open_rate')} /></b></span>
                <span>Click rate <b className="text-grayscale-1300"><Pct v={rateOf(run, 'click_rate')} /></b></span>
                <span>CTOR <b className="text-grayscale-1300"><Pct v={rateOf(run, 'ctor')} /></b></span>
                <span>Signed in <b className="text-grayscale-1300">{pctText(sr)}</b> of delivered</span>
                {'auto_opened' in run && <span>Auto opens only <b className="text-grayscale-1300">{num(run.auto_opened)}</b></span>}
                {'bot_clicked' in run && <span>Bot clicks only <b className="text-grayscale-1300">{num(run.bot_clicked)}</b></span>}
              </div>
              <EngagementNote eng={d.engagement} automated={!!automated} />
            </Section>
            <Section title="Links clicked"
              right={<span className="text-xs text-grayscale-1100">every link in the invitation is tracked</span>}>
              <LinkRankTable rows={links} empty="Nobody has clicked a link in these invites yet." />
            </Section>
            <KPIRow>
              {FILTERS.map(f => (
                <KPI key={f.key} label={f.label} value={fmtN(counts[f.key] ?? 0)}
                  onClick={() => setStatus(v => (v === f.key && f.key !== 'all' ? 'all' : f.key))}
                  active={status === f.key && f.key !== 'all'} />
              ))}
            </KPIRow>
            <DataTable rows={shown} csvName={`invite-run-${runId}`} searchable maxHeight="60vh"
              columns={['email', 'name', 'status', 'delivered', 'opened_at', 'clicked_at', 'automation', 'links',
                'signed_in_at', 'error']}
              optionalColumns={['at', 'clicks', 'bounced', 'unsubscribed', 'spam_report', 'team', 'user_id']}
              unsortable={['automation']}
              labels={{ opened_at: 'Opened (IST)', clicked_at: 'Clicked (IST)', links: 'Links clicked', automation: 'Automated',
                signed_in_at: 'Signed in (IST)', at: 'Sent (IST)', spam_report: 'Spam report', user_id: 'User id' }}
              render={{
                email: r => (
                  canOpen(r) ? (
                    <button type="button" title="Open this person's timeline" onClick={() => openTimeline(r)}
                      className="text-left hover:underline underline-offset-2">{r.email || str(r.user_id)}</button>
                  ) : <span className="text-grayscale-1100">—</span>
                ),
                name: r => <span>{r.name || '—'}</span>,
                status: statusBadge,
                delivered: r => flag(r.delivered),
                opened_at: r => flag(r.opened, r.opened_at),
                clicked_at: r => (
                  <span className="inline-flex items-center gap-1">
                    {flag(r.clicked, r.clicked_at)}
                    {num(r.clicks) > 1 && <span className="text-[11px] text-grayscale-1100">×{num(r.clicks)}</span>}
                  </span>
                ),
                links: r => (r.links.length
                  ? <span className="text-xs" title={r.links.join('\n')}>{r.links[0]}{r.links.length > 1 ? ` +${r.links.length - 1}` : ''}</span>
                  : <span className="text-grayscale-1100">—</span>),
                automation: r => (Array.isArray(r.eng_tags) && r.eng_tags.length
                  ? <span className="inline-flex gap-1 flex-wrap"><EngTags row={r} /></span>
                  : <span className="text-grayscale-1100">—</span>),
                signed_in_at: r => flag(r.signed_in, r.signed_in_at),
                at: r => <span>{fmtIST(r.at)}</span>,
                bounced: r => flag(r.bounced),
                unsubscribed: r => flag(r.unsubscribed),
                spam_report: r => flag(r.spam_report),
                team: r => (r.team ? <Badge tone="grey">team</Badge> : <span className="text-grayscale-1100">—</span>),
                error: r => (str(r.error)
                  ? <span className="text-xs text-red-700" title={str(r.error)}>{str(r.error)}</span>
                  : <span className="text-grayscale-1100">—</span>),
              }}
              actions={r => (canOpen(r) ? <Btn small title="Everything we sent this person, and what they did"
                onClick={() => openTimeline(r)}>Timeline</Btn> : null)}
              empty={status !== 'all' ? 'Nobody in this run matches that card.'
                : 'No recipients recorded for this run in the period.'} />
            <div className="text-[11px] text-grayscale-1100 flex flex-col gap-1">
              <span>
                Delivered, opened and clicked come from SendGrid's events for each invitation
                {automated ? ', every open and click counted' : ', people only: instant reads, Apple MPP and scanner opens and bot clicks are tagged in "Automated" instead'}.
                Signed in = the person's first sign-in after the invite, within {num(attr)} days.
              </span>
              {group && (
                <span onClickCapture={onClose}>
                  <AdminLink to={{ group: 'email', tab: 'dashboard', bulk: str(group.id) }}
                    title="The Email Center dashboard narrowed to this group: every e-mail they were sent">
                    Every e-mail to {str(group.label)} on the Dashboard →</AdminLink>
                </span>
              )}
            </div>
          </>
        )}
      </Drawer>
      {timeline && <Timeline {...timeline} onClose={() => setTimeline(null)} />}
    </>
  );
};

export default InviteRunDrawer;
